/**
 * User Rituals Service
 * API service for fetching user teachings, practices, coachings, and booked sessions
 */

import type { MyBooking, MyBookingsResponse } from "@/features/booking/services/booking.service"
import { apiClient } from "@/lib/api-client"

export type BookingItem = MyBooking
export type BookingsResponse = MyBookingsResponse

export interface RitualListItem {
  id: string
  title: string
  type: "IN_PERSON" | "COURSE" | "SELF_GUIDED" | "SELF_GUIDED_COURSE"
  participationFee: "Free" | "Paid" | "Donation-based"
  duration: number
  date: Date
  createdBy: {
    id: string
    username: string | null
    name: string | null
    coverImage: string | null
  } | null
  uploads?: Array<{
    id: string
    filePath: string
    fileName: string
    isCover: boolean
  }>
  isSaved: boolean
  isArchived?: boolean
  description?: string
  followersCount?: number
  location?: string
  price?: number | null
  currency?: string | null
  meetingLink?: string | null
  viewCount?: number
  joinedCount?: number
  savedCount?: number
  earnings?: number
  minimumParticipants?: number | null
}

export interface RitualStatusCounts {
  active: number
  upcoming: number
  completed: number
  draft: number
  archived: number
}

export interface RitualListResponse {
  rituals: RitualListItem[]
  total: number
  page: number
  totalPages: number
  hasMore: boolean
  counts?: RitualStatusCounts
}

interface RitualListParams {
  page?: number
  limit?: number
  search?: string
  subTab?: string
}

function buildQueryString(params?: RitualListParams): string {
  if (!params) return ""
  const qs = new URLSearchParams()
  if (params.page) qs.append("page", params.page.toString())
  if (params.limit) qs.append("limit", params.limit.toString())
  if (params.search) qs.append("search", params.search)
  if (params.subTab) qs.append("subTab", params.subTab)
  const str = qs.toString()
  return str ? `?${str}` : ""
}

export async function getUserRitualPractices(
  userId: string,
  params?: RitualListParams,
): Promise<RitualListResponse> {
  const url = `/api/rituals/user/${userId}/practices${buildQueryString(params)}`
  const response = await apiClient.get<{
    success: boolean
    data: RitualListResponse
    message: string
  }>(url)
  return response.data.data
}

export async function getUserRitualTeachings(
  userId: string,
  params?: RitualListParams,
): Promise<RitualListResponse> {
  const url = `/api/rituals/user/${userId}/teachings${buildQueryString(params)}`
  const response = await apiClient.get<{
    success: boolean
    data: RitualListResponse
    message: string
  }>(url)
  return response.data.data
}

export async function getUserRitualCoachings(
  userId: string,
  params?: RitualListParams,
): Promise<RitualListResponse> {
  const url = `/api/rituals/user/${userId}/coachings${buildQueryString(params)}`
  const response = await apiClient.get<{
    success: boolean
    data: RitualListResponse
    message: string
  }>(url)
  return response.data.data
}

export async function getUserBookedSessions(params?: RitualListParams): Promise<BookingsResponse> {
  const url = `/api/bookings/mine${buildQueryString(params)}`
  const response = await apiClient.get<{
    success: boolean
    data: BookingsResponse
    message: string
  }>(url)
  return response.data.data
}
